import { Select as SelectPrimitive } from '@base-ui/react/select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';

import { cn } from '../../cn';

// Intentional silent coercion: Base UI's Select.Root.onValueChange has the
// signature (value: string | null, eventDetails) => void. The null value is
// emitted in two scenarios (verified against `SelectPositioner` and
// `SelectRoot` in the installed `@base-ui/react/select` build):
//   1. Programmatic clear — caller passes value={null} as a controlled prop.
//   2. Items collection mutates and the controlled value is no longer in the
//      list — Base UI resets to the initial value if available, otherwise
//      to null. This is the realistic trigger in this app, e.g. when
//      ExceptionsPage's EXCEPTION_TYPES list shrinks.
// (Pressing Escape closes the popup but does NOT touch the value — the
// SelectRoot module has zero `setValue(null)` paths from key events.)
// Every consumer in this codebase uses useState<string> and treats null and
// '' as the same "no selection" state; DriverSelector even renders
// <SelectItem value=""> as its explicit clear-item. We coalesce null → ''
// here so consumers can keep their (value: string) => void handlers, and the
// eventDetails parameter is dropped on the floor because no caller needs it.
// If a future consumer must distinguish "user picked the empty option" from
// "selection cleared", drop this wrapper and import @base-ui/react/select
// directly.
type SelectRootProps = Omit<SelectPrimitive.Root.Props<string>, 'onValueChange'> & {
  onValueChange?: (value: string) => void;
};

function Select({ onValueChange, ...props }: SelectRootProps) {
  return (
    <SelectPrimitive.Root<string>
      onValueChange={onValueChange ? (value) => onValueChange(value ?? '') : undefined}
      {...props}
    />
  );
}

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

function SelectTrigger({ className, children, ...props }: SelectPrimitive.Trigger.Props) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      className={cn(
        'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon render={<ChevronDown className="h-4 w-4 opacity-50" />} />
    </SelectPrimitive.Trigger>
  );
}

function SelectScrollUpButton({ className, ...props }: SelectPrimitive.ScrollUpArrow.Props) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-up-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronUp className="h-4 w-4" />
    </SelectPrimitive.ScrollUpArrow>
  );
}

function SelectScrollDownButton({ className, ...props }: SelectPrimitive.ScrollDownArrow.Props) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronDown className="h-4 w-4" />
    </SelectPrimitive.ScrollDownArrow>
  );
}

// Positioner-only props are derived from Base UI's own type — not enumerated
// by hand — so any future addition to the anchor-positioning surface (or to
// Select-specific props like alignItemWithTrigger) flows in automatically.
// `Omit<Positioner.Props, keyof Popup.Props>` strips the named keys that
// both share via BaseUIComponentProps (className / children / render / ref /
// style / id / role / tabIndex / aria-*-named / on*-handlers). The `data-*`
// index signature passes through Omit, so callers can still set `data-*`
// attributes via positionerProps if they need them on the Positioner level.
type SelectPositionerOnlyProps = Omit<
  SelectPrimitive.Positioner.Props,
  keyof SelectPrimitive.Popup.Props
>;

type SelectContentProps = SelectPrimitive.Popup.Props & {
  /** Forwarded to Positioner. Lets callers tune side/align/collision/etc. */
  positionerProps?: SelectPositionerOnlyProps;
  /** Extra classes for Positioner — primarily for z-index override when nested in another portal/modal. */
  positionerClassName?: string;
};

function SelectContent({
  className,
  children,
  positionerProps,
  positionerClassName,
  // Lift portal-owner attribution to the Positioner so click-outside detectors
  // using `target.closest('[data-portal-owner]')` (e.g. QueryBuilder) catch
  // every interactive descendant — including SelectScrollUpArrow /
  // SelectScrollDownArrow, which are siblings of Popup under Positioner.
  // Spreading `...props` only onto Popup would leave those siblings outside
  // the owned subtree.
  'data-portal-owner': dataPortalOwner,
  ...props
}: SelectContentProps & { 'data-portal-owner'?: string }) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        sideOffset={4}
        // Disable Base UI's default item-align-with-trigger behavior so the
        // popup behaves like the prior Radix Select.Content (popper-style
        // adjacent placement) — matches every consumer's pre-migration
        // expectation. Flip back to default if a redesigned dropdown UX
        // wants the selected item to overlay the trigger text.
        alignItemWithTrigger={false}
        {...positionerProps}
        className={cn('z-50', positionerClassName)}
        data-portal-owner={dataPortalOwner}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Popup
          data-slot="select-content"
          className={cn(
            'relative z-50 max-h-[var(--available-height)] min-w-[max(8rem,var(--anchor-width))] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[open]:animate-in data-[closed]:animate-out data-[closed]:fade-out-0 data-[open]:fade-in-0 data-[closed]:zoom-out-95 data-[open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
            className,
          )}
          {...props}
        >
          <div className="p-1">{children}</div>
        </SelectPrimitive.Popup>
        <SelectScrollDownButton />
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({ className, ...props }: SelectPrimitive.GroupLabel.Props) {
  return (
    <SelectPrimitive.GroupLabel
      data-slot="select-label"
      className={cn('py-1.5 pl-8 pr-2 text-sm font-semibold', className)}
      {...props}
    />
  );
}

function SelectItem({ className, children, ...props }: SelectPrimitive.Item.Props) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-surface-2 focus:text-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>

      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({ className, ...props }: SelectPrimitive.Separator.Props) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('-mx-1 my-1 h-px bg-muted', className)}
      {...props}
    />
  );
}

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
